/**
 * Waiter (staff handheld) wire types and calls. Field names are the backend's (snake_case), copied from
 * `services/core-api/src/modules/{staff,cart,assistance}`.
 *
 * Money a waiter may see (locked product decision, DECISION_LOG `HARD-DEC-15`): menu item prices, the cart's
 * subtotal and total, and the session's bill total, because customers ask the waiter for them. They are
 * integer minor units exactly as the backend sent them; nothing here adds, rounds or converts.
 *
 * Everything else that carries an amount is still dropped here, at the edge, so it can never reach a waiter
 * screen: the cart's tax and per-line amounts, and the menu's tax rate. Payments, tenders, the paid amount,
 * the remaining balance, discounts and adjustments are not in any waiter response at all.
 */
import { staffApi } from '@/api/instances';
import type { ApiClient } from '@/api/client';

// ─── Floor: GET /staff/floor ─────────────────────────────────────────────────

export interface FloorSession {
  session_id: string;
  party_display_name: string;
  party_size: number;
  status: string;
  duration_minutes: number;
  has_pending_orders: boolean;
  has_active_assistance: boolean;
  /** The session's bill total so far, in minor units. 0 while nothing has been ordered. */
  bill_total: number;
}

export interface FloorLocation {
  location_id: string;
  location_label: string;
  /** `TABLE` or `SEAT`. */
  location_type: string;
  zone_id: string | null;
  zone_name: string | null;
  nominal_capacity: number;
  status: string;
  active_sessions: FloorSession[];
}

// ─── Sessions: GET /staff/active-sessions, GET /staff/sessions/{id} ──────────

export interface ActiveSession {
  session_id: string;
  status: string;
  party_display_name: string;
  guest_count: number;
  opened_at: string;
  last_activity_at: string | null;
  location: { location_id: string; label: string; location_type: string; zone_name: string | null };
  has_assistance_request: boolean;
  assistance_request_types: string[];
  cart: {
    revision: number;
    has_items: boolean;
    item_count: number;
    total_quantity: number;
    waiter_added_count: number;
    updated_at: string | null;
  };
}

export type CartAddedBy = 'CUSTOMER' | 'WAITER';
export type CartLineIssue = 'ITEM_UNAVAILABLE' | 'INVALID_MODIFIERS';

export interface CartLine {
  cart_item_id: string;
  menu_item_id: string;
  name: string | null;
  quantity: number;
  notes: string | null;
  /** Option names only. */
  modifiers: string[];
  is_available: boolean;
  issue: CartLineIssue | null;
  added_by: CartAddedBy;
  added_at: string;
}

export interface Cart {
  session_id: string;
  revision: number;
  updated_at: string | null;
  item_count: number;
  total_quantity: number;
  is_valid: boolean;
  /** Draft cart amounts in minor units, as sent: before tax, and with tax. Not yet ordered, not a bill. */
  subtotal_amount: number;
  total_amount: number;
  items: CartLine[];
}

export interface StaffSession extends ActiveSession {
  draft_cart: Cart;
}

interface WireCartLine extends Omit<CartLine, 'modifiers'> {
  modifiers: { modifier_option_id: string; name: string; price_delta: number }[];
  unit_amount: number | null;
  line_amount: number | null;
}
interface WireCart extends Omit<Cart, 'items'> {
  tax_amount: number;
  items: WireCartLine[];
}

/** Keeps only what a waiter may see: the cart's subtotal and total, no tax line and no per-line amount. */
export function toCart(wire: WireCart): Cart {
  return {
    session_id: wire.session_id,
    revision: wire.revision,
    updated_at: wire.updated_at,
    item_count: wire.item_count,
    total_quantity: wire.total_quantity,
    is_valid: wire.is_valid,
    subtotal_amount: wire.subtotal_amount,
    total_amount: wire.total_amount,
    items: wire.items.map((line) => ({
      cart_item_id: line.cart_item_id,
      menu_item_id: line.menu_item_id,
      name: line.name,
      quantity: line.quantity,
      notes: line.notes,
      modifiers: line.modifiers.map((m) => m.name),
      is_available: line.is_available,
      issue: line.issue,
      added_by: line.added_by,
      added_at: line.added_at,
    })),
  };
}

// ─── Menu: GET /staff/menu ───────────────────────────────────────────────────

export interface ModifierOption {
  id: string;
  name: string;
  /** What choosing this adds to (or takes off) the item price, in minor units. 0 for no change. */
  price_delta: number;
}

export interface ModifierGroup {
  id: string;
  name: string;
  min_selection: number;
  max_selection: number;
  options: ModifierOption[];
}

export interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  /** The branch's price for the item in minor units, as sent. */
  base_price: number;
  is_available: boolean;
  modifier_groups: ModifierGroup[];
}

export interface MenuCategory {
  id: string;
  name: string;
  sort_order: number;
  items: MenuItem[];
}

export interface StaffMenu {
  categories: MenuCategory[];
}

interface WireMenu {
  categories: {
    id: string;
    name: string;
    sort_order: number;
    items: {
      id: string;
      name: string;
      description: string | null;
      base_price: number;
      is_available: boolean;
      modifier_groups: {
        id: string;
        name: string;
        min_selection: number;
        max_selection: number;
        options: { id: string; name: string; price_delta: number }[];
      }[];
    }[];
  }[];
}

export function toMenu(wire: WireMenu): StaffMenu {
  return {
    categories: wire.categories.map((category) => ({
      id: category.id,
      name: category.name,
      sort_order: category.sort_order,
      items: category.items.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        base_price: item.base_price,
        is_available: item.is_available,
        modifier_groups: item.modifier_groups.map((group) => ({
          id: group.id,
          name: group.name,
          min_selection: group.min_selection,
          max_selection: group.max_selection,
          options: group.options.map((option) => ({
            id: option.id,
            name: option.name,
            price_delta: option.price_delta,
          })),
        })),
      })),
    })),
  };
}

// ─── Ready to serve: GET /staff/orders/ready ─────────────────────────────────

export interface ReadyOrderItem {
  order_item_id: string;
  name: string;
  quantity: number;
  /** `READY`, or `SERVED` for a line already delivered on its own. */
  fulfillment_status: string;
  modifiers: string[];
  notes: string | null;
}

/** An order the kitchen has finished, waiting for a waiter. No amounts are sent. */
export interface ReadyOrder {
  order_id: string;
  order_number: string;
  session_id: string;
  location_id: string;
  location_label: string;
  /** `TABLE` or `SEAT`. */
  location_type: string;
  zone_name: string | null;
  party_name: string;
  notes: string | null;
  ready_at: string;
  items: ReadyOrderItem[];
}

export interface ServeResult {
  order_id: string;
  order_number: string;
  order_status: string;
  /** Empty when the order had already been served: the server returned it without a change. */
  served_item_ids: string[];
  updated_at: string;
}

// ─── Writes ──────────────────────────────────────────────────────────────────

export interface AddToCartInput {
  menu_item_id: string;
  quantity: number;
  modifiers: { modifier_option_id: string }[];
  notes?: string;
  /** The cart revision the waiter was looking at. A changed cart is refused, never merged. */
  expected_revision: number;
}

export interface AssistanceRequest {
  request_id: string;
  type: string;
  note: string | null;
  status: string;
  created_at: string;
  resolved_at: string | null;
  session_id: string;
  party_display_name: string;
  location_id: string;
  location_label: string;
  location_type: string;
  zone_name: string | null;
}

export interface OpenSessionInput {
  location_id: string;
  display_name: string;
  party_size: number;
}

export interface OpenedSession {
  session_id: string;
  location_id: string;
  location_label: string;
  status: string;
}

export interface TransferInput {
  session_id: string;
  to_location_id: string;
  reason?: string;
}

export interface TransferResult {
  session_id: string;
  previous_location_id: string;
  previous_location_label: string;
  new_location_id: string;
  new_location_label: string;
  new_location_type: string;
  new_zone_name: string | null;
  status: string;
}

export function createStaffApi(client: ApiClient) {
  return {
    getFloor: async (signal?: AbortSignal): Promise<FloorLocation[]> => {
      const wire = await client.get<FloorLocation[]>('/staff/floor', { signal });
      return wire.map((location) => ({
        ...location,
        active_sessions: location.active_sessions.map((session) => ({
          session_id: session.session_id,
          party_display_name: session.party_display_name,
          party_size: session.party_size,
          status: session.status,
          duration_minutes: session.duration_minutes,
          has_pending_orders: session.has_pending_orders,
          has_active_assistance: session.has_active_assistance,
          bill_total: session.bill_total,
        })),
      }));
    },

    getActiveSessions: (signal?: AbortSignal) =>
      client.get<ActiveSession[]>('/staff/active-sessions', { signal }),

    getSession: async (sessionId: string, signal?: AbortSignal): Promise<StaffSession> => {
      const wire = await client.get<ActiveSession & { draft_cart: WireCart }>(
        `/staff/sessions/${encodeURIComponent(sessionId)}`,
        { signal },
      );
      return { ...wire, draft_cart: toCart(wire.draft_cart) };
    },

    getMenu: async (signal?: AbortSignal): Promise<StaffMenu> =>
      toMenu(await client.get<WireMenu>('/staff/menu', { signal })),

    /** Adds to the customer's shared cart. This never places an order. */
    addToCart: async (
      sessionId: string,
      input: AddToCartInput,
      idempotencyKey: string,
    ): Promise<Cart> =>
      toCart(
        await client.post<WireCart>(
          `/staff/sessions/${encodeURIComponent(sessionId)}/cart/items`,
          input,
          { idempotencyKey },
        ),
      ),

    getReadyOrders: (signal?: AbortSignal) =>
      client.get<ReadyOrder[]>('/staff/orders/ready', { signal }),

    /** Marks a READY order served. Serving an order that is already served returns it unchanged. */
    serveOrder: (orderId: string) =>
      client.post<ServeResult>(`/staff/orders/${encodeURIComponent(orderId)}/serve`),

    getAssistance: (signal?: AbortSignal) =>
      client.get<AssistanceRequest[]>('/staff/assistance', { signal }),

    resolveAssistance: (requestId: string) =>
      client.post<AssistanceRequest>(`/staff/assistance/${encodeURIComponent(requestId)}/resolve`, {
        status: 'COMPLETED',
      }),

    openSession: (input: OpenSessionInput, idempotencyKey: string) =>
      client.post<OpenedSession>('/staff/sessions', input, { idempotencyKey }),

    transfer: (fromLocationId: string, input: TransferInput) =>
      client.post<TransferResult>(
        `/staff/locations/${encodeURIComponent(fromLocationId)}/transfer`,
        input,
      ),
  };
}

export const staffFloorApi = createStaffApi(staffApi);
